import { translateBackend } from '@assets/i18n/config';
import {
  type ConfigurableTablePage,
  FeatureFlagTypes,
} from '@generated/graphql';

import { Drawer } from '@mui/material';

import type {
  Section,
  SubSection,
} from '@ovComponents/2-component/navSection/navSection';
import {
  getSubSectionName,
  isNavSubSectionPathActive,
  NavSection,
} from '@ovComponents/2-component/navSection/navSection';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalStats } from '@providers/globalStatsHooks';
import {
  UserContext,
  useFeatureFlag,
  useOrganizationFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Box, IconButton, useTheme } from 'hephaestus/web';
import {
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Settings,
  Start as StartIcon,
  TableChart as TableChartIcon,
} from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  handleAnalytics,
  sectionsConfig,
  settingsSectionsConfig,
} from './config';
import { useLayoutTokens } from './layout.tokens';

export const useMenuSections = () => {
  const { userContext, activeOrganization } = useContext(UserContext);
  const canWriteAdvancedOrganizationSettings = usePermission(
    'write:advanced_organization_settings',
  );
  const canReadCustodianActivityBasic = usePermission(
    'read:custodian_activity_basic',
  );
  const location = useLocation();
  const configurableHomePagesEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ConfigurableHomePages,
  );
  const configurableTablePagesEnabled = useFeatureFlag(
    FlagsmithFlags.configurable_table_pages,
  );
  const accountGroupsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AccountGroups,
  );
  const portfolioTradingPrototypeEnabled = useFeatureFlag(
    FlagsmithFlags.portfolio_trading_prototype,
  );
  const reconFlagsDashboardEnabled = useFeatureFlag(
    FlagsmithFlags.recon_flags_dashboard,
  );
  const reconActivitiesDashboardEnabled = useFeatureFlag(
    FlagsmithFlags.recon_activities_dashboard,
  );
  const useFeeEnableToggleEnabled = useFeatureFlag(
    FlagsmithFlags.use_fee_enable_toggle,
  );
  const translatedAttributesEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.TranslatedAttributes,
  );
  const formMapperEnabled = useFeatureFlag(FlagsmithFlags.form_mapper);
  const alertBannersEnabled = useFeatureFlag(FlagsmithFlags.alert_banners);
  const textVariantsEnabled = useFeatureFlag(FlagsmithFlags.text_variants);
  const repcodeAccessEnabledInFlagsmith = useFeatureFlag(
    FlagsmithFlags.repcode_access,
  );
  const configurableClientReportsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ConfigurableClientReports,
  );

  const hasHomePage =
    userContext.role?.homePageConfiguration && configurableHomePagesEnabled;

  const dashboardsSection = {
    name: hasHomePage ? 'menu.dashboards' : 'menu.dashboard',
    icon: DashboardIcon,
    subSections:
      userContext.role?.dashboards?.map((x: any) => ({
        name: translateBackend(x.translatedName),
        path: `/dashboard/${x.id}`,
        accessKey: 'HOME',
      })) ?? [],
  };

  const homePageSection = {
    name: 'menu.homePage',
    icon: HomeIcon,
    subSections: [
      {
        name: userContext.role?.homePageConfiguration?.translatedName,
        path: '/',
        accessKey: 'HOME',
      },
    ],
  };

  const settingsSection = {
    name: 'menu.settings',
    icon: Settings,
    newTab: true,
    beta: true,
    subSections: [
      {
        name: 'menu.settings',
        path: '/settings/general',
        newTab: true,
      },
    ],
  };

  const configurableTablePagesSections: Section[] = [];
  if (
    configurableTablePagesEnabled &&
    (userContext.role?.configurableTablePages?.length ?? 0) > 0
  ) {
    const configurableTablePages =
      userContext.role?.configurableTablePages ?? [];
    const groupedPages: Record<string, any> = configurableTablePages.reduce(
      (acc: any, page: ConfigurableTablePage) => {
        const sectionName = page.section.name;
        const sectionTranslatedName = translateBackend(sectionName);
        if (!acc[sectionTranslatedName]) {
          acc[sectionTranslatedName] = {
            sectionName,
            icon: page.section.icon || TableChartIcon,
            pages: [],
          };
        }
        acc[sectionTranslatedName].pages.push({
          name: page.name,
          path: `/configurableTablePage/${page.id}`,
          configurableTablePage: true,
          permissions: [
            'read:configurable_table_page',
            'read:configurable_table_data',
          ],
        });
        return acc;
      },
      {},
    );

    // Convert grouped pages to sections based on count
    Object.values(groupedPages).forEach((group: any) => {
      if (group.pages.length === 1) {
        // Single page: use section name as direct menu item
        configurableTablePagesSections.push({
          name: group.sectionName,
          icon: group.icon,
          subSections: [
            {
              name: group.sectionName,
              path: group.pages[0].path,
              permissions: group.pages[0].permissions,
              configurableTablePage: group.pages[0].configurableTablePage,
            },
          ],
        });
      } else {
        // Multiple pages: create expandable section with page names as subsections
        configurableTablePagesSections.push({
          name: group.sectionName,
          icon: group.icon,
          subSections: group.pages,
        });
      }
    });
  }

  let sections = sectionsConfig;

  if (!accountGroupsEnabled) {
    sections = sections.map((section) => {
      if (section.name === 'menu.clientManagement') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.accountGroupsList',
          ),
        };
      }
      return section;
    });
  }

  sections = sections
    .map((section) => {
      if (section.name !== 'menu.reconciliation') {
        return section;
      }
      return {
        ...section,
        subSections: section.subSections.filter((subsec: SubSection) => {
          if (subsec.name === 'menu.flags' && !reconFlagsDashboardEnabled) {
            return false;
          }
          if (
            subsec.name === 'menu.activities' &&
            (!reconActivitiesDashboardEnabled || !canReadCustodianActivityBasic)
          ) {
            return false;
          }
          return true;
        }),
      };
    })
    .filter(
      (section) =>
        section.name !== 'menu.reconciliation' ||
        section.subSections.length > 0,
    );

  if (!portfolioTradingPrototypeEnabled) {
    sections = sections.map((section) => {
      if (section.name !== 'menu.trading') {
        return section;
      }
      return {
        ...section,
        subSections: section.subSections.filter(
          (subsec: SubSection) => subsec.name !== 'menu.rebalanceDashboard',
        ),
      };
    });
  }

  if (
    useFeeEnableToggleEnabled &&
    activeOrganization.feeSetting &&
    !activeOrganization.feeSetting.enabled
  ) {
    sections = sections.filter(
      (section) => section.name !== 'menu.feeAndBilling',
    );
  }

  const defaultSections = [
    dashboardsSection,
    ...sections,
    ...configurableTablePagesSections,
  ];

  if (hasHomePage) {
    defaultSections.unshift(homePageSection);
  }

  if (canWriteAdvancedOrganizationSettings) {
    defaultSections.push(settingsSection);
  }
  let settingsSections = settingsSectionsConfig;
  if (!reconFlagsDashboardEnabled) {
    settingsSections = settingsSections
      .map((section) => {
        if (section.name !== 'menu.reconciliation') {
          return section;
        }
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.flagPolicies',
          ),
        };
      })
      .filter(
        (section) =>
          section.name !== 'menu.reconciliation' ||
          section.subSections.length > 0,
      );
  }

  if (!configurableHomePagesEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.homePageConfigurations',
          ),
        };
      }
      return section;
    });
  }

  if (!translatedAttributesEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.customAttributeLabels',
          ),
        };
      }
      return section;
    });
  }

  if (!repcodeAccessEnabledInFlagsmith) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.accessUsers') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.repCodes',
          ),
        };
      }
      return section;
    });
  }

  if (!configurableTablePagesEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) =>
              subsec.name !== 'menu.configurableTablePages',
          ),
        };
      }
      return section;
    });
  }

  if (!formMapperEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.formMapper',
          ),
        };
      }
      return section;
    });
  }
  if (!alertBannersEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.alertBanners',
          ),
        };
      }
      return section;
    });
  }

  if (!textVariantsEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.theme') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.textDesigner',
          ),
        };
      }
      return section;
    });
  }

  if (useFeeEnableToggleEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.feesAndBilling') {
        return {
          ...section,
          subSections: section.subSections.map((subsec: SubSection) => ({
            ...subsec,
            permissions: [...(subsec.permissions ?? []), 'read:fee_setting'],
          })),
        };
      }
      return section;
    });
  }

  if (!configurableClientReportsEnabled) {
    settingsSections = settingsSections.map((section) => {
      if (section.name === 'menu.productExperience') {
        return {
          ...section,
          subSections: section.subSections.filter(
            (subsec: SubSection) => subsec.name !== 'menu.clientReports',
          ),
        };
      }
      return section;
    });
  }

  return location.pathname.includes('/settings')
    ? settingsSections
    : defaultSections;
};

const Sidebar = (props: any) => {
  const { closed, setClosed, accessiblePages } = useContext(UserContext);
  const theme = useTheme();
  const layoutTokens = useLayoutTokens(theme);
  const globalStats = useGlobalStats();
  const location = useLocation();
  const { window } = props;
  const { mobileOpen } = props;
  const { setMobileOpen } = props;
  const [sectionsOpen, setSectionsOpen] = useState<any[]>([]);
  const [openedMenu, setOpenedMenu] = useState<string>('');
  const { sendAnalytic } = useContext(AnalyticsContext);

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDrawerClick = (name: string) => {
    handleAnalytics(name, sendAnalytic);
    setMobileOpen(false);
  };

  const finalSections: Section[] = useMenuSections();

  useEffect(() => {
    const initiallyOpenSections: any[] = Array(finalSections.length).fill(
      false,
    );
    finalSections.forEach((sec, idx) => {
      if (sec.subSections && sec.subSections.length > 1) {
        const matchingPaths = sec.subSections.filter((subsec: SubSection) =>
          isNavSubSectionPathActive(subsec, location.pathname),
        );
        if (matchingPaths.length > 0) {
          initiallyOpenSections[idx] = true;
        }
      }
    });
    setSectionsOpen(initiallyOpenSections);
  }, [finalSections.forEach, finalSections.length, location.pathname]);

  const calculateActiveSection = () => {
    const activeSection = finalSections.find((section) => {
      if (section.subSections && section.subSections.length > 0) {
        return section.subSections.some((subsec: SubSection) =>
          isNavSubSectionPathActive(subsec, location.pathname),
        );
      }
      return false;
    });

    if (activeSection) {
      const activeSectionIndex = finalSections.indexOf(activeSection);
      const newSectionsOpen = Array(finalSections.length).fill(false);
      newSectionsOpen[activeSectionIndex] = true;
      setSectionsOpen(newSectionsOpen);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: this is a side effect that needs to be run when the pathname changes
  useEffect(() => {
    calculateActiveSection();
  }, [location.pathname, closed]);

  const displayStats = (s: SubSection) =>
    !!(s.stats && globalStats[s.stats] && globalStats[s.stats] > 0);

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        flexWrap: 'inherit',
        mt: { xs: 2, lg: 0 },
        overflowX: 'visible',
        overflowY: closed ? 'inherit' : 'auto',
      }}
    >
      <Box
        sx={{
          overflow: 'visible',
        }}
      >
        {finalSections.map((section, index) => (
          <Box
            key={getSubSectionName(section.name)}
            onMouseEnter={() => setOpenedMenu(getSubSectionName(section.name))}
            data-testid={kebabCase(getSubSectionName(section.name))}
            sx={{
              overflow: 'visible',
            }}
          >
            <NavSection
              section={section}
              accessiblePages={accessiblePages}
              handleDrawerClick={handleDrawerClick}
              globalStats={globalStats}
              displayStats={displayStats}
              showSection={false}
              setSectionsOpen={setSectionsOpen}
              sectionsOpen={sectionsOpen}
              index={index}
              location={location}
              closed={closed && !mobileOpen}
              openedMenu={openedMenu === getSubSectionName(section.name)}
            />
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <IconButton
          aria-label='start'
          onClick={() => setClosed(!closed)}
          size='small'
          sx={{ m: 1 }}
        >
          <StartIcon
            sx={{
              transform: !closed ? 'rotate(180deg)' : undefined,
              transition: 'all 0.4s',
              color: theme.color.onSurface,
            }}
            fontSize='inherit'
          />
        </IconButton>
      </Box>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box onMouseLeave={() => setOpenedMenu('')}>
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        container={container}
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: 'block',
            md: 'block',
            lg: 'none',
          },
          // The AppBar sits at drawer + 1 so its shadow can fall on the
          // permanent nav; this modal drawer has to clear it or the top 72px
          // of the panel (and its backdrop) render under the bar.
          zIndex: (t) => t.zIndex.drawer + 2,
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width: closed && !mobileOpen ? '48px' : '240px',
            backgroundColor: layoutTokens.sidebarColor,
            transition: 'all 0.4s',
            overflow: 'visible',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant='permanent'
        sx={{
          display: {
            xs: 'none',
            sm: 'none',
            md: 'none',
            lg: 'block',
          },
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width: closed && !mobileOpen ? '52px' : '240px',
            backgroundColor: layoutTokens.sidebarColor,
            transition: 'all 0.4s',
            overflow: 'visible',
            // Pin under 72px top bar. Avoid mt+height+pt calc (old 76 = 64
            // bar + 12 pt; bar grew to 72 and overflowed bottom by pt).
            top: 72,
            bottom: 0,
            height: 'auto',
            pt: 1.5,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
